import { Bell, LogOut, Plus, Search, Settings, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui';
import { NotificationBell } from './notification-bell';
import { openGlobalSearch } from './global-search';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { canSeeSettings } from './settings-nav';
import logoUrl from '@/assets/suzuki-logo.png';

/**
 * Agent top bar. Primary navigation lives in the sidebar, so this is a utility
 * bar only: identity, search, create, alerts, account.
 *
 * Not centred with `mx-auto max-w-*`: the sidebar sits beside this bar, so
 * centring the row pushed the logo away from the app's left edge and left the
 * brand floating in the middle of nowhere.
 */
export function Header() {
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  const { t } = useI18n();

  return (
    <header className="shrink-0 border-b border-line bg-surface text-ink">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-search focus:rounded-md focus:bg-brand-600 focus:px-3 focus:py-2 focus:text-white"
      >
        {t('shell.skipToMain')}
      </a>

      <div className="flex h-14 items-center gap-3 px-4">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex shrink-0 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label={t('shell.homeLabel')}
        >
          <img src={logoUrl} alt="" className="h-7 w-auto" />
          <span className="hidden border-l border-line pl-2.5 text-base font-semibold text-brand-900 sm:inline">
            {t('shell.supportDesk')}
          </span>
        </button>

        {/* Opens the existing global search overlay (also bound to '/'). */}
        <button
          type="button"
          onClick={openGlobalSearch}
          className="hidden h-9 min-w-0 max-w-md flex-1 items-center gap-2 rounded-md border border-line-strong bg-surface-page px-3 text-left text-ink-subtle transition-colors hover:border-brand-300 hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:flex"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate text-base">{t('shell.searchPlaceholder')}</span>
          <kbd className="ml-auto shrink-0 rounded border border-line-strong bg-surface px-1.5 font-mono text-xs text-ink-subtle">
            /
          </kbd>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {can('create') && (
            <Button size="sm" onClick={() => navigate('/tickets/new')}>
              <Plus aria-hidden="true" />
              <span className="hidden sm:inline">{t('shell.newTicket')}</span>
            </Button>
          )}
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger aria-label={t('shell.userMenu')}>
              <div className="flex items-center gap-2 rounded-md px-1.5 py-1.5 text-ink transition-colors hover:bg-surface-subtle">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <User className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="hidden max-w-32 truncate text-base font-medium lg:inline">
                  {user?.name || t('common.user')}
                </span>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/notification-preferences')}>
                <Bell className="mr-2 h-4 w-4" />
                {t('shell.notificationPreferences')}
              </DropdownMenuItem>
              {canSeeSettings(can) && (
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('shell.settings')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  void logout().then(() => navigate('/login', { replace: true }));
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('shell.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
